import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Spinner } from "@/components/common";
import DocViewer from "@/components/DocViewer";
import { lbl, SECTION_LABELS } from "@/constants/labels";
import { FileText, Eye, Check, X, Clock3, Printer } from "lucide-react";

function ImprimirTodo({ vid }) {
  const [busy, setBusy] = useState(false);
  const imprimir = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/vinculaciones/${vid}/expediente-completo`, { responseType: "blob" });
      const src = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const f = document.createElement("iframe");
      f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
      f.onload = () => { try { f.contentWindow.focus(); f.contentWindow.print(); } catch { window.open(src, "_blank"); } };
      f.src = src;
      document.body.appendChild(f);
      setTimeout(() => f.remove(), 180000);
      const om = Number(res.headers["x-omitidos"] || 0);
      toast.success(`Expediente listo: ${res.headers["x-incluidos"]} documentos${om ? ` (${om} no imprimibles, ver índice)` : ""}`);
    } catch (err) {
      let msg = "No se pudo preparar la impresión";
      try { msg = JSON.parse(await err.response.data.text()).detail || msg; } catch { /* sin detalle */ }
      toast.error(msg);
    } finally { setBusy(false); }
  };
  return (
    <button onClick={imprimir} disabled={busy} data-testid="btn-imprimir-expediente" className="flex items-center gap-1.5 rounded-lg bg-vino-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-vino-800 disabled:opacity-60">
      {busy ? <Spinner className="h-3.5 w-3.5" /> : <Printer className="h-3.5 w-3.5" />} {busy ? "Preparando…" : "Imprimir todo el expediente"}
    </button>
  );
}

const SIMPLE = ["datos_personales", "salud", "perfil"];
const LIST = ["familia", "contactos_emergencia", "referencias_laborales", "referencias_familiares", "referencias_no_familiares", "academica"];
const fecha = (f) => (f ? new Date(f).toLocaleString("es-CO") : "");

function Solicitud({ s, onDone }) {
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  const resolver = async (accion) => {
    setBusy(true);
    try { await api.post(`/solicitudes-hv/${s.id}/${accion}`, { motivo }); toast.success(accion === "aprobar" ? "Cambios aprobados y aplicados" : "Solicitud rechazada"); onDone(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4" data-testid={`solicitud-card-${s.id}`}>
      <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-amber-800"><Clock3 className="h-4 w-4" /> {s.tipo === "directorio" ? "Cambio sobre datos ya registrados por HUMAN" : "Solicitud de actualización de hoja de vida"} · {s.solicitante} · {fecha(s.fecha)}</p>
      <div className="mb-3 overflow-x-auto">
        <table className="w-full min-w-[420px] text-xs">
          <thead><tr className="text-left text-slate-500"><th className="py-1">Campo</th><th>Valor actual</th><th>Valor propuesto</th></tr></thead>
          <tbody>{s.cambios.map((c, i) => <tr key={i} className="border-t border-amber-100"><td className="py-1 font-medium text-slate-700">{lbl(c.campo)}</td><td className="text-slate-500">{c.antes || "—"}</td><td className="font-semibold text-slate-800">{c.despues || "—"}</td></tr>)}</tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo (obligatorio para rechazar)" className="min-w-[200px] flex-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs outline-none focus:border-vino-500" data-testid="solicitud-motivo" />
        <button onClick={() => resolver("rechazar")} disabled={busy} data-testid="solicitud-rechazar" className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"><X className="h-3.5 w-3.5" /> Rechazar</button>
        <button onClick={() => resolver("aprobar")} disabled={busy} data-testid="solicitud-aprobar" className="flex items-center gap-1 rounded-lg bg-vino-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-vino-800"><Check className="h-3.5 w-3.5" /> Aprobar</button>
      </div>
    </div>
  );
}

function Seccion({ titulo, children }) {
  return <div><h4 className="mb-2 text-sm font-semibold text-slate-700">{titulo}</h4>{children}</div>;
}

function Campos({ obj }) {
  const items = Object.entries(obj || {});
  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
      {items.map(([k, v]) => <div key={k}><p className="text-xs text-slate-400">{lbl(k)}</p><p className="break-words text-sm font-medium text-slate-800">{v || <span className="text-slate-300">—</span>}</p></div>)}
    </div>
  );
}

function Lista({ rows }) {
  if (!rows?.length) return <p className="text-sm text-slate-400">Sin registros.</p>;
  return <div className="space-y-2">{rows.map((r, i) => <div key={i} className="rounded-lg border border-slate-200 p-3"><Campos obj={r} /></div>)}</div>;
}

function DocsLista({ e, onVer }) {
  const docs = [...(e.documentos || []).map((d) => ({ ...d, _t: d.categoria, _s: d.original_filename })), ...(e.documentos_contratacion || []).map((d) => ({ ...d, _t: d.nombre, _s: `Contratación · v${d.version || 1}` }))];
  if (!docs.length) return <p className="text-sm text-slate-400">Sin documentos adjuntos.</p>;
  return (
    <div className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="hv-empleado-docs">
      {docs.map((d) => (
        <div key={d.id} className="flex items-center gap-3 p-3">
          <FileText className="h-4 w-4 text-vino-700" />
          <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-slate-800">{d._t}</p><p className="truncate text-xs text-slate-400">{d._s}</p></div>
          <button onClick={() => onVer(d)} data-testid={`hv-empleado-ver-${d.id}`} className="flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-vino-700 hover:bg-vino-50"><Eye className="h-3.5 w-3.5" /> Ver</button>
        </div>
      ))}
    </div>
  );
}

export function HojaVidaEmpleado({ vid, soloDocumentos = false }) {
  const [r, setR] = useState(undefined);
  const [ver, setVer] = useState(null);
  const [denegado, setDenegado] = useState(false);
  const load = () => api.get(`/vinculaciones/${vid}/hoja-vida`).then((x) => setR(x.data.expediente)).catch((err) => { setDenegado(err.response?.status === 403); setR(null); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vid]);
  if (r === undefined) return <Spinner className="mx-auto h-6 w-6 text-vino-700" />;
  if (denegado) return <p className="py-8 text-center text-sm text-slate-400" data-testid="hv-empleado-sin-permiso">Tu rol no tiene permiso para consultar la hoja de vida del empleado.</p>;
  if (!r) return <p className="py-8 text-center text-sm text-slate-400" data-testid="hv-empleado-vacia">Este empleado aún no tiene hoja de vida digital en Roge.</p>;
  const pendientes = (r.solicitudes || []).filter((s) => s.estado === "pendiente");
  const url = ver && (ver.contratacion_id ? `/contrataciones/${ver.contratacion_id}/documentos/${ver.id}/preview` : `/expedientes/${r.id}/documentos/${ver.id}/download`);
  return (
    <div className="space-y-6" data-testid="hv-empleado">
      {!soloDocumentos && <p className="text-xs text-slate-500">Información diligenciada por el empleado en su portal, leída directamente de su hoja de vida (fuente única). Expediente: {r.estado}.</p>}
      {!soloDocumentos && pendientes.map((s) => <Solicitud key={s.id} s={s} onDone={load} />)}
      {!soloDocumentos && SIMPLE.map((s) => <Seccion key={s} titulo={SECTION_LABELS[s] || lbl(s)}><Campos obj={r.hoja_vida[s]} /></Seccion>)}
      {!soloDocumentos && LIST.map((s) => <Seccion key={s} titulo={SECTION_LABELS[s] || lbl(s)}><Lista rows={r.hoja_vida[s]} /></Seccion>)}
      {!soloDocumentos && <Seccion titulo="Beneficiarios"><Lista rows={(r.beneficiarios?.lista || []).map(({ id, ...b }) => b)} /></Seccion>}
      <Seccion titulo="Documentos y soportes"><div className="mb-2 flex justify-end"><ImprimirTodo vid={vid} /></div><DocsLista e={r} onVer={setVer} /></Seccion>
      {!soloDocumentos && (
        <Seccion titulo="Historial de cambios de la hoja de vida">
          {(r.historial_hv || []).length === 0 ? <p className="text-sm text-slate-400">Sin cambios registrados.</p> : (
            <div className="max-h-72 space-y-1 overflow-y-auto text-xs" data-testid="hv-empleado-historial">
              {r.historial_hv.map((h, i) => <p key={i} className="text-slate-600"><span className="text-slate-400">{fecha(h.fecha)} · {h.usuario}</span> — <b>{lbl(h.campo)}</b>: «{h.antes || "—"}» → «{h.despues || "—"}»</p>)}
            </div>
          )}
        </Seccion>
      )}
      {ver && <DocViewer url={url} titulo={ver._t} subtitulo={ver._s} nombreArchivo={ver.original_filename || `${ver.codigo || "documento"}.pdf`} onClose={() => setVer(null)} />}
    </div>
  );
}
